import React from 'react';
import { CircularProgress, Typography } from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import whiteLogo from '/icons/logo-white.svg?url';
import { OrangePrimaryButton } from 'app/components/form/OrangePrimaryButton.tsx';
import { useLogin } from 'app/hooks/useLogin.ts';
import { hasSignedInBefore } from '@/authConfig.ts';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nCoreNs } from '@/i18n/dictionaries/interfaces.ts';

const useExternalInstance =
  window.location.search === '?external=true' || window.location.pathname === '/loginExternal';

const SUPPORT_EMAIL = 'support@sklls.ai';

/** Microsoft's four-square logo. The hexes are fixed by Microsoft's brand guidelines. */
const MicrosoftLogo = () => (
  <svg width={18} height={18} viewBox="0 0 21 21" aria-hidden="true">
    <rect x="0" y="0" width="10" height="10" fill="#F25022" />
    <rect x="11" y="0" width="10" height="10" fill="#7FBA00" />
    <rect x="0" y="11" width="10" height="10" fill="#00A4EF" />
    <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
  </svg>
);

const AppUnauthenticated = () => {
  const { instance } = useMsal();
  const [translate] = useTranslation(I18nNamespace.Core);
  const { redirecting, needsBackendConsent, signIn, signInAndConsent } = useLogin({
    instance,
    guest: !useExternalInstance
  });

  // The login page only renders for signed-out users (MSAL's UnauthenticatedTemplate),
  // so there's never a cached account here. Instead we read a marker persisted at
  // sign-in time: if this browser has signed in before, greet them with "Welcome back".
  const isReturningVisitor = hasSignedInBefore();

  // Capture the current requested path (excluding special routes)
  React.useEffect(() => {
    // An MSAL redirect return carries the auth response in the fragment —
    // never capture that as a deep link.
    if (/[#&](code|state|error)=/.test(window.location.hash)) {
      return;
    }
    const path = window.location.pathname + window.location.search + window.location.hash;
    if (path !== '/' && path !== '/loginExternal') {
      localStorage.setItem('postLoginRedirectPath', path);
    }
  }, []);

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-background-dark-600">
      <img
        src="/sklls_rays.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -top-2 -right-[400px] w-[1150px] max-w-none opacity-60 select-none"
      />
      <header className="relative flex items-center px-11 pt-7">
        <img src={whiteLogo} alt="Sklls" className="h-7.5 w-auto" />
      </header>

      <main className="relative flex flex-1 flex-col items-center justify-center px-4 py-10">
        <div className="w-full max-w-108 rounded-3xl bg-white px-10 pt-10 pb-8 shadow-[0_40px_80px_-32px_rgba(0,0,0,0.55)] max-sm:px-5">
          <Typography component="h1" sx={{ fontSize: '1.75rem', fontWeight: 500, lineHeight: 1.3, color: 'grey.900' }}>
            {translate(
              isReturningVisitor
                ? nameof.full<I18nCoreNs>((n) => n.login.headingReturning)
                : nameof.full<I18nCoreNs>((n) => n.login.heading)
            )}
          </Typography>

          {needsBackendConsent ? (
            <div className="mt-7 flex flex-col gap-4">
              <Typography variant="body1" sx={{ color: 'grey.700' }}>
                {translate(nameof.full<I18nCoreNs>((n) => n.login.consentTitle))}
              </Typography>
              <OrangePrimaryButton variant="contained" onClick={() => signInAndConsent()}>
                {translate(nameof.full<I18nCoreNs>((n) => n.login.consentButton))}
              </OrangePrimaryButton>
            </div>
          ) : (
            <div className="mt-7 flex flex-col gap-3.5">
              <button
                type="button"
                onClick={() => signIn()}
                disabled={redirecting}
                aria-busy={redirecting}
                className="flex h-12 w-full cursor-pointer items-center justify-center gap-3 rounded-lg border border-grey-200 bg-white transition hover:border-grey-400 hover:shadow-panel disabled:cursor-default disabled:border-grey-100 disabled:hover:shadow-none"
              >
                {redirecting ? <CircularProgress size={18} color="secondary" /> : <MicrosoftLogo />}
                <Typography variant="body1" component="span" sx={{ fontWeight: 500, color: 'grey.600' }}>
                  {redirecting
                    ? translate(nameof.full<I18nCoreNs>((n) => n.login.redirectingToMicrosoft))
                    : translate(nameof.full<I18nCoreNs>((n) => n.login.signInWithMicrosoft))}
                </Typography>
              </button>

              <div className="flex items-start gap-2">
                <Typography variant="caption" sx={{ color: 'grey.500' }}>
                  {translate(nameof.full<I18nCoreNs>((n) => n.login.helperLine))}
                </Typography>
              </div>
            </div>
          )}

          <hr className="my-6 border-grey-100" />

          <Typography variant="subtitle2" component="h2" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {translate(nameof.full<I18nCoreNs>((n) => n.login.noAccessTitle))}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: 'grey.500' }}>
            {translate(nameof.full<I18nCoreNs>((n) => n.login.noAccessBody))}{' '}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="font-medium text-secondary-600 no-underline hover:text-secondary-700"
            >
              {SUPPORT_EMAIL}
            </a>
            .
          </Typography>
        </div>
      </main>

      <footer className="relative flex items-center justify-center gap-5 px-11 pb-7">
        <a
          href="https://sklls.ai/app-privacy/"
          className="text-white/55 no-underline transition-colors hover:text-white"
        >
          <Typography variant="caption" component="span" sx={{ color: 'inherit' }}>
            {translate(nameof.full<I18nCoreNs>((n) => n.login.privacyTerms))}
          </Typography>
        </a>
        <a href="https://sklls.ai" className="text-white/55 no-underline transition-colors hover:text-white">
          <Typography variant="caption" component="span" sx={{ color: 'inherit' }}>
            sklls.ai
          </Typography>
        </a>
      </footer>
    </div>
  );
};

export default AppUnauthenticated;
